/**
 * Storage Encryption Utility
 * Provides basic encryption for sensitive data stored in localStorage/sessionStorage
 * Note: This is client-side encryption and should not be used for highly sensitive data
 * For production, consider using a more robust encryption library or server-side storage
 */

/**
 * Simple XOR encryption (basic obfuscation)
 * Note: This is NOT cryptographically secure, but provides basic protection against casual XSS
 * @param {string} text - Text to encrypt
 * @param {string} key - Encryption key
 * @returns {string} Encrypted text (base64 encoded)
 */
const simpleEncrypt = (text, key) => {
  if (!text || !key) return text;
  
  let result = '';
  const keyLength = key.length;
  
  for (let i = 0; i < text.length; i++) {
    const charCode = text.charCodeAt(i) ^ key.charCodeAt(i % keyLength);
    result += String.fromCharCode(charCode);
  }
  
  // Base64 encode for safe storage
  try {
    return btoa(result);
  } catch (e) {
    return text; // Fallback if encoding fails
  }
};

/**
 * Simple XOR decryption
 * @param {string} encryptedText - Encrypted text (base64 encoded)
 * @param {string} key - Decryption key
 * @returns {string} Decrypted text
 */
const simpleDecrypt = (encryptedText, key) => {
  if (!encryptedText || !key) return encryptedText;
  
  try {
    // Base64 decode
    const text = atob(encryptedText);
    let result = '';
    const keyLength = key.length;
    
    for (let i = 0; i < text.length; i++) {
      const charCode = text.charCodeAt(i) ^ key.charCodeAt(i % keyLength);
      result += String.fromCharCode(charCode);
    }
    
    return result;
  } catch (e) {
    return encryptedText; // Fallback if decryption fails
  }
};

/**
 * Get encryption key (derived from user ID or use default)
 * In production, consider using a more secure key derivation method
 */
const getEncryptionKey = () => {
  // Try to get user ID from auth state
  try {
    const auth = require('../firebaseConfig').auth;
    if (auth?.currentUser?.uid) {
      return auth.currentUser.uid.substring(0, 16); // Use first 16 chars of UID
    }
  } catch (e) {
    // Fallback if auth not available
  }
  
  // Fallback key (should be replaced with user-specific key in production)
  return 'thebox-sec-key';
};

/**
 * Encrypt and store data in localStorage
 * @param {string} key - Storage key
 * @param {any} data - Data to store
 * @param {boolean} encrypt - Whether to encrypt (default: true)
 */
export const setSecureStorage = (key, data, encrypt = true) => {
  try {
    const jsonData = JSON.stringify(data);
    const encrypted = encrypt ? simpleEncrypt(jsonData, getEncryptionKey()) : jsonData;
    localStorage.setItem(key, encrypted);
  } catch (err) {
    console.error('Failed to store data:', err);
    // Fallback to unencrypted storage
    localStorage.setItem(key, JSON.stringify(data));
  }
};

/**
 * Retrieve and decrypt data from localStorage
 * @param {string} key - Storage key
 * @param {boolean} encrypted - Whether data is encrypted (default: true)
 * @returns {any} Decrypted data or null
 */
export const getSecureStorage = (key, encrypted = true) => {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    
    const decrypted = encrypted ? simpleDecrypt(stored, getEncryptionKey()) : stored;
    return JSON.parse(decrypted);
  } catch (err) {
    console.error('Failed to retrieve data:', err);
    // Try to parse as unencrypted JSON
    try {
      return JSON.parse(localStorage.getItem(key));
    } catch (e) {
      return null;
    }
  }
};

/**
 * Remove data from localStorage
 * @param {string} key - Storage key
 */
export const removeSecureStorage = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (err) {
    console.error('Failed to remove data:', err);
  }
};

/**
 * Clear all encrypted storage (useful for logout)
 */
export const clearSecureStorage = () => {
  try {
    // Clear all rate limit entries
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key.startsWith('rate_limit_') || key.startsWith('secure_')) {
        localStorage.removeItem(key);
      }
    });
  } catch (err) {
    console.error('Failed to clear secure storage:', err);
  }
};

